import { Download, FileText, Loader2, Trash2, Upload } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/shadcn/index.js';
import { Button } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import {
  type Attachment,
  ATTACHMENT_ALLOWED_MIME,
  ATTACHMENT_MAX_BYTES,
  deleteAttachment,
  fetchAttachmentObjectUrl,
  isImageMime,
  listAttachments,
  uploadAttachment,
} from '../../lib/attachments.js';
import { cn } from '../../lib/cn.js';
import { formatDate } from '../../lib/intl.js';

/** Shared with PartyFilesTab — keep one implementation. */
export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export interface FileUploadDialogProps {
  entityType: string;
  entityId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  /** Fires whenever the attachment set changes (after upload/delete/load). */
  onChange?: (attachments: Attachment[]) => void;
}

/**
 * Reusable attachments modal: drag-and-drop / picker upload (multi-file,
 * client-validated against the server allow-list + 25 MB cap), plus a list of
 * existing files with image previews, download, and delete. Wired to the
 * generic `/attachments` API for any entityType/entityId.
 */
export function FileUploadDialog({
  entityType,
  entityId,
  open,
  onOpenChange,
  title,
  onChange,
}: FileUploadDialogProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const canWrite = usePermission('attachments.write');
  const canDelete = usePermission('attachments.delete');
  const inputRef = useRef<HTMLInputElement>(null);
  const [items, setItems] = useState<Attachment[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const list = await listAttachments(entityType, entityId);
      setItems(list);
      onChange?.(list);
    } catch {
      setItems([]);
    }
  }, [entityType, entityId, onChange]);

  useEffect(() => {
    if (!open) return;
    setItems(null);
    setError(null);
    void refresh();
  }, [open, refresh]);

  const validate = useCallback(
    (file: File): string | null => {
      if (!file.type || !ATTACHMENT_ALLOWED_MIME.has(file.type)) {
        return t('fileUpload.errors.mime', { defaultValue: 'That file type is not allowed.' });
      }
      if (file.size > ATTACHMENT_MAX_BYTES) {
        return t('fileUpload.errors.size', { defaultValue: 'Each file must be 25 MB or smaller.' });
      }
      return null;
    },
    [t],
  );

  const uploadFiles = useCallback(
    async (files: FileList | File[]): Promise<void> => {
      setError(null);
      const arr = Array.from(files);
      const firstBad = arr.map((f) => ({ f, e: validate(f) })).find((x) => x.e);
      if (firstBad) setError(`${firstBad.f.name}: ${firstBad.e}`);
      const valid = arr.filter((f) => !validate(f));
      if (valid.length === 0) return;
      setBusy(true);
      try {
        for (const f of valid) await uploadAttachment(entityType, entityId, f);
        await refresh();
      } catch {
        setError(t('fileUpload.errors.generic', { defaultValue: 'Upload failed. Try again.' }));
      } finally {
        setBusy(false);
      }
    },
    [entityType, entityId, refresh, validate, t],
  );

  const onRemove = useCallback(
    async (id: string): Promise<void> => {
      setBusy(true);
      setError(null);
      try {
        await deleteAttachment(id);
        await refresh();
      } catch {
        setError(t('fileUpload.errors.generic', { defaultValue: 'Upload failed. Try again.' }));
      } finally {
        setBusy(false);
      }
    },
    [refresh, t],
  );

  const onDownload = useCallback(async (a: Attachment): Promise<void> => {
    const url = await fetchAttachmentObjectUrl(a.id);
    const link = document.createElement('a');
    link.href = url;
    link.download = a.filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    // Give the download a tick to start before reclaiming the blob.
    window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{title ?? t('fileUpload.title', { defaultValue: 'Files' })}</DialogTitle>
          <DialogDescription>
            {t('fileUpload.subtitle', { defaultValue: 'Upload images or documents — up to 25 MB each.' })}
          </DialogDescription>
        </DialogHeader>

        {canWrite && (
          <div
            role="button"
            tabIndex={0}
            aria-label={t('fileUpload.pick', { defaultValue: 'Choose files' })}
            className={cn(
              'flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-7 text-center transition',
              dragOver ? 'border-brand bg-brand/5' : 'border-border bg-bg-subtle hover:border-brand/60',
            )}
            onClick={() => inputRef.current?.click()}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              if (e.dataTransfer.files?.length) void uploadFiles(e.dataTransfer.files);
            }}
          >
            <input
              ref={inputRef}
              type="file"
              multiple
              accept={[...ATTACHMENT_ALLOWED_MIME].join(',')}
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) void uploadFiles(e.target.files);
                if (e.target) e.target.value = '';
              }}
            />
            {busy ? (
              <Loader2 className="h-6 w-6 animate-spin text-brand" aria-hidden />
            ) : (
              <Upload className="h-6 w-6 text-fg-muted" aria-hidden />
            )}
            <div className="text-sm font-medium text-fg">
              {busy
                ? t('fileUpload.uploading', { defaultValue: 'Uploading…' })
                : t('fileUpload.dropHint', { defaultValue: 'Drop files here or click to browse' })}
            </div>
            <div className="text-xs text-fg-muted">
              {t('fileUpload.accepts', { defaultValue: 'Images, PDF, and Office documents' })}
            </div>
          </div>
        )}

        {error && <p className="text-sm text-danger" role="alert">{error}</p>}

        <div className="max-h-72 overflow-auto">
          {items === null ? (
            <p className="py-6 text-center text-sm text-fg-muted">{t('actions.loading')}</p>
          ) : items.length === 0 ? (
            <p className="py-6 text-center text-sm text-fg-muted">
              {t('fileUpload.empty', { defaultValue: 'No files yet.' })}
            </p>
          ) : (
            <ul className="flex flex-col gap-2">
              {items.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center gap-3 rounded-lg border border-border bg-bg p-2"
                >
                  <AttachmentThumb attachment={a} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-fg">{a.filename}</div>
                    <div className="text-xs text-fg-muted">
                      {formatBytes(a.sizeBytes)} · {formatDate(a.uploadedAt, { locale: i18n.language })}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => void onDownload(a)}
                    aria-label={t('fileUpload.download', { defaultValue: 'Download' })}
                    title={t('fileUpload.download', { defaultValue: 'Download' })}
                  >
                    <Download className="h-4 w-4" aria-hidden />
                  </Button>
                  {canDelete && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => void onRemove(a.id)}
                      disabled={busy}
                      aria-label={t('fileUpload.remove', { defaultValue: 'Remove' })}
                      title={t('fileUpload.remove', { defaultValue: 'Remove' })}
                    >
                      <Trash2 className="h-4 w-4 text-danger" aria-hidden />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Small 40×40 preview — image attachments load as an auth-fetched object URL. */
export function AttachmentThumb({ attachment }: { attachment: Attachment }): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const isImg = isImageMime(attachment.mimeType);

  useEffect(() => {
    if (!isImg) return undefined;
    let active = true;
    let made: string | null = null;
    void fetchAttachmentObjectUrl(attachment.id)
      .then((u) => {
        if (active) {
          made = u;
          setUrl(u);
        } else {
          URL.revokeObjectURL(u);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [attachment.id, isImg]);

  return (
    <div className="grid h-10 w-10 flex-none place-items-center overflow-hidden rounded-md border border-border bg-bg-subtle">
      {isImg && url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : (
        <FileText className="h-5 w-5 text-fg-muted" aria-hidden />
      )}
    </div>
  );
}
